import {
  discrepanciesResponseSchema,
  messagesResponseSchema,
  type DiscrepanciesResponse,
  type MessagesResponse,
  type DiscrepancyType,
  type Discrepancy,
} from '../types/discrepancy';
import { ApiClientError } from '@/errors';
import {
  authFetch, getAuthHeaders, API_BASE_URL 
} from '@/lib/api-client';

export async function getDiscrepancies(siteId: string): Promise<DiscrepanciesResponse> {
  const response = await authFetch(`${API_BASE_URL}/v1/sites/${siteId}/discrepancies`);
  if (!response.ok) throw ApiClientError.fromResponse(response, 'Failed to fetch discrepancies');
  return discrepanciesResponseSchema.parse(await response.json());
}

export async function getDiscrepancyMessages(discrepancyId: string): Promise<MessagesResponse> {
  const response = await authFetch(`${API_BASE_URL}/v1/discrepancies/${discrepancyId}/messages`);
  if (!response.ok) throw ApiClientError.fromResponse(response, 'Failed to fetch messages');
  return messagesResponseSchema.parse(await response.json());
}

export interface CreateDiscrepancyPayload {
  poId: string;
  deliveryId?: string;
  type: DiscrepancyType;
  description: string;
  items: Array<{
    lineId: string;
    affectedQuantity: number;
  }>;
}

export async function createDiscrepancy(payload: CreateDiscrepancyPayload): Promise<Discrepancy> {
  const response = await authFetch(`${API_BASE_URL}/v1/discrepancies`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw ApiClientError.fromResponse(response, 'Failed to create discrepancy');
  return response.json() as Promise<Discrepancy>;
}

export async function sendMessage(discrepancyId: string, content: string, attachments?: File[]): Promise<void> {
  const formData = new FormData();
  formData.append('content', content);
  if (attachments) {
    attachments.forEach((file) => formData.append('attachments', file));
  }

  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/v1/discrepancies/${discrepancyId}/messages`, {
    method: 'POST',
    headers: { Authorization: headers.Authorization as string },
    body: formData,
  });
  if (!response.ok) throw ApiClientError.fromResponse(response, 'Failed to send message');
}

export async function acceptResolution(discrepancyId: string): Promise<void> {
  const response = await authFetch(`${API_BASE_URL}/v1/discrepancies/${discrepancyId}/accept`, {method: 'POST',});
  if (!response.ok) throw ApiClientError.fromResponse(response, 'Failed to accept resolution');
}

export async function rejectResolution(discrepancyId: string, reason: string): Promise<void> {
  const response = await authFetch(`${API_BASE_URL}/v1/discrepancies/${discrepancyId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) throw ApiClientError.fromResponse(response, 'Failed to reject resolution');
}
